import useCompanies from "../useCompanies";
import { useEffect, useState } from "react";
import useCompanyBranches from "../useCompanyBranches";
import useBranchPeople from "../useBranchPeople";
import { CombinedSummary, TreeData } from "./types";
import convertCombinedSummaryToTreeData from "./convertCombinedSummaryToTreeData";
import insertPeopleIntoBranch from "./insertPeopleIntoBranch";
import insertBranchesIntoCompany from "./insertBranchesIntoCompany";
import convertCompaniesToCombined from "./convertCompaniesToCombined";
import { MoneyCorpPermissions } from "../usePermissions/usePermissions";

/**
 * Oh look: a comment...
 *
 * Create a sparsely-populated tree of data, showing companies -> company -> branch -> user.
 * New parts of the tree are loaded when new selectedCompany and selectedBranch values are
 * passed in.
 *
 * The counts are used to refresh data at the company/branch/user level. When the counts
 * change, the data at that level is updated.
 *
 * Detail in a sub-branch is set to "Loading...", just in case it becomes visible
 * when loading a sub-branch.
 *
 * @param searchText string to filter results by
 * @param selectedCompany new company to load
 * @param selectedBranch new branch to load
 * @param companyChangeCount update this to reload the companies
 * @param branchChangeCount update this to reload the branches in the current company
 * @param userChangeCount update this to reload the users in the current branch
 * @param permissions whether you can see branches, companies or everything
 */
const useCompanyTree = (
  selectedCompany: string | undefined,
  selectedBranch: number | undefined,
  companyChangeCount: number,
  branchChangeCount: number,
  userChangeCount: number,
  permissions: MoneyCorpPermissions
) => {
  const { companies } = useCompanies([companyChangeCount]);
  const [combined, setCombined] = useState<CombinedSummary>({});

  const { companyBranches } = useCompanyBranches(selectedCompany, permissions, [
    branchChangeCount
  ]);
  const { branchPeople } = useBranchPeople(
    selectedCompany,
    selectedBranch,
    permissions,
    [userChangeCount]
  );
  const [treeData, setTreeData] = useState<TreeData>({ companies: [] });

  useEffect(() => {
    setCombined(convertCompaniesToCombined(companies));
  }, [companies]);

  useEffect(() => {
    if (selectedCompany) {
      setCombined((combined: any) => {
        return insertBranchesIntoCompany(
          combined,
          selectedCompany,
          companyBranches
        );
      });
    }
  }, [selectedCompany, companyBranches]);

  useEffect(() => {
    if (selectedCompany && selectedBranch) {
      setCombined((combined: CombinedSummary) => {
        return insertPeopleIntoBranch(
          combined,
          selectedCompany,
          selectedBranch,
          branchPeople
        );
      });
    }
  }, [selectedCompany, selectedBranch, branchPeople]);

  useEffect(() => {
    setTreeData(convertCombinedSummaryToTreeData(combined));
  }, [combined]);

  return treeData;
};

export default useCompanyTree;
